import * as React from 'react';

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import type { VariantProps } from 'tailwind-variants';

import CancelRoundedIcon from '@mui/icons-material/CancelRounded';

import cn from '../../utils/cn';
import { chipVariants, removeIconVariant } from '../Chip/Chip';

export type ChipGroupProps = VariantProps<typeof chipVariants>;

const ChipGroupContext = React.createContext<VariantProps<typeof chipVariants>>({
  size: 'regular',
  variant: 'default',
  disabled: false,
  color: 'primary',
});

const ChipGroupProvider = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> & VariantProps<typeof chipVariants>
>(({ className, color, variant, size, disabled, children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root ref={ref} className={cn('flex items-center justify-center gap-1', className)} {...props}>
    <ChipGroupContext.Provider value={{ variant, size, disabled, color }}>{children}</ChipGroupContext.Provider>
  </ToggleGroupPrimitive.Root>
));

ChipGroupProvider.displayName = ToggleGroupPrimitive.Root.displayName;

const ChipGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof chipVariants> & {
      icon?: React.ReactNode;
      deleteIcon?: boolean;
      onDeleteClick?: () => void;
    }
>(({ className, children, color, variant, icon, size, deleteIcon, disabled, onDeleteClick, ...props }, ref) => {
  const context = React.useContext(ChipGroupContext);

  const customIcon = (newIcon: React.ReactNode) => {
    if (!newIcon) return null;
    return React.cloneElement(newIcon as React.ReactElement, {
      className: cn((newIcon as React.ReactElement)?.props?.className || 'size-4 mr-2'),
    });
  };

  const pressRemoveClick = onDeleteClick
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        onDeleteClick();
      }
    : undefined;

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      disabled={context.disabled || disabled}
      className={cn(
        chipVariants({
          color: context.color || color,
          variant: context.variant || variant,
          size: context.size || size,
          disabled: context.disabled || disabled,
        }),
        className
      )}
      {...props}
    >
      {customIcon(icon)} {children}{' '}
      {deleteIcon && (
        <CancelRoundedIcon
          onClick={pressRemoveClick}
          data-testid='remove-icon'
          className={cn(
            '-mr-1 ml-2 ',
            removeIconVariant({
              hover: color,
              size: size,
            })
          )}
        />
      )}
      {deleteIcon === false && <span data-testid='remove-icon-placeholder' className='-mr-1 ml-1 size-4' />}
    </ToggleGroupPrimitive.Item>
  );
});

ChipGroup.displayName = ToggleGroupPrimitive.Item.displayName;

export { ChipGroupProvider };

export default ChipGroup;
